/**
 * @fileOverview "Speichern & neu" and "Speichern & kopieren" on the create pages: after a create the
 * page stays on its form, opened anew or filled with a copy of what was just saved, and a notice names
 * the new record with a way to it. See docs/navigation.md.
 */

import {
  type InjectionKey,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  readonly,
  ref,
  shallowRef,
} from "vue"
import { type RouteLocationRaw, NavigationFailureType, isNavigationFailure, useRoute, useRouter } from "vue-router"
import { snapshot } from "@/composables/useUnsavedChanges"
import { hasUnsavedChanges } from "@/services/unsavedChanges"
import { useResponsiveLayout } from "@/composables/useResponsiveLayout"

/** What follows a create: the new record's page (Speichern, Ctrl+S), an empty form, or a copy. */
export type CreateSeriesMode = "open" | "new" | "copy"

export interface CreateSeriesNotice {
  id: number
  /** The record as the notice names it. */
  name: string
  /** Its page, for Öffnen. */
  to: RouteLocationRaw
}

/**
 * Remounts the view on screen when it changes: App.vue keys the router view with it. The next form is
 * then the view set up afresh, exactly as a first visit builds it — the defaults it fills in, its first
 * tab, the top of the page, no field marked invalid — instead of a reset each view would have to keep in
 * step with its own setup. It changes nowhere else, so every other page keeps its component as before.
 */
const round = ref(0)
export const createSeriesRound = readonly(round)

/** Shown by LewCreateSeriesNotice, mounted once in App.vue so it outlives the remount. */
export const createSeriesNotice = shallowRef<CreateSeriesNotice | null>(null)
let noticeId = 0

/**
 * Handed from the form that saved to the one the next round mounts. Stamped with the route and round
 * it is meant for and taken at the first look, so it can never fill a later visit's form.
 */
let handoff: { route: unknown; round: number; seed: unknown } | null = null

const CREATE_SERIES: InjectionKey<{ request: (_mode: CreateSeriesMode | null) => void }> = Symbol("lew:create-series")

/** The create page's series, for its Speichern & neu button; null on a detail page. */
export const useCreateSeriesContext = () => inject(CREATE_SERIES, null)

export interface CreateSeriesOptions<S> {
  /** What the form holds. Copied when the request goes out: several stores swap the answer in. */
  state: () => S
  /** The next form's starting point, made from what was saved (services/createSeriesCopy.js). */
  copy: (_saved: S) => S
  /**
   * Query parameters the next form keeps — the employee's `type`. The others go: a `duplicateOf` or a
   * packet's `itemId` filled this form, not the next one.
   */
  keepQuery?: string[]
}

export interface CreateSeries<S> {
  /** The copy this form starts from, or null. A form filled from it keeps no draft. */
  seed: S | null
  /** What the next save ends on. Read once at the start of the save, so Ctrl+S always opens the record. */
  take: () => CreateSeriesMode
  /** A plain copy of the form as it is now. */
  snapshot: () => S
  /**
   * After a create that was asked to go on (call `markSaved()` first): opens the next form and the
   * notice once the save has settled, and true. False when it does not go on — Speichern or Ctrl+S, the
   * form left meanwhile, something else on the page still unsaved — and the caller opens the new record
   * as Speichern does: the form holds a record that is saved now, and a second Speichern would create it
   * twice.
   */
  continue: (_options: { next: CreateSeriesMode; saved: S; name: string; to: RouteLocationRaw }) => boolean
}

const FIELD = ["input:not([type=hidden]):not([type=file]):not([type=checkbox]):not([type=radio])", "textarea"]
  .map(selector => `${selector}:not([disabled]):not([readonly])`)
  .join(",")

/** The first field the user types into: in the tab on screen, or in the card of a page without tabs. */
function firstField(): HTMLInputElement | HTMLTextAreaElement | null {
  const scope =
    document.querySelector(".v-main .v-window-item--active") ?? document.querySelector(".v-main .console-card")
  const fields = scope ? [...scope.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(FIELD)] : []
  return fields.find(field => field.offsetParent !== null) ?? null
}

/** For a create view. Provides itself to the Speichern & neu button in its edit component. */
export function useCreateSeries<S>({ state, copy, keepQuery = [] }: CreateSeriesOptions<S>): CreateSeries<S> {
  const route = useRoute()
  const router = useRouter()
  const formRoute = route.name

  const arrived = handoff?.route === formRoute && handoff.round === round.value ? handoff : null
  handoff = null
  const seed = (arrived?.seed ?? null) as S | null

  let requested: CreateSeriesMode | null = null
  let mounted = true
  onBeforeUnmount(() => (mounted = false))

  // Typing goes on where it started: the first field, the copy's name selected so it can be typed over.
  // Not on touch, as nowhere in the app: a field focused by itself throws the keyboard over the form.
  const { isTouch } = useResponsiveLayout()
  if (arrived && !isTouch.value) {
    onMounted(() =>
      nextTick(() =>
        requestAnimationFrame(() => {
          const field = firstField()
          field?.focus()
          if (seed) field?.select()
        })
      )
    )
  }

  function continueSeries({ next, saved, name, to }: Parameters<CreateSeries<S>["continue"]>[0]) {
    // A save that answers after the form was left must not remount the page now on screen. Something
    // else on the page with edits (a report panel beside the form) would go with the remount unasked:
    // then Speichern's way, whose navigation asks.
    if (next === "open" || !mounted || route.name !== formRoute || hasUnsavedChanges.value) return false
    const seed = next === "copy" ? copy(saved) : null
    // Once the save that asked for it has settled, not inside it: its last steps belong to this form,
    // under this form's route — `submit` dismissing a draft on offer once the save went through would
    // otherwise run against the next form, or against a draft key the dropped query has just switched
    // on. They are promise steps, all done before the next task.
    setTimeout(async () => {
      if (!mounted || route.name !== formRoute) return
      handoff = { route: formRoute, round: round.value + 1, seed }
      const query = Object.fromEntries(Object.entries(route.query).filter(([key]) => keepQuery.includes(key)))
      // The route first, the round after: the next form reads its query (useDuplicateSeed, the packet
      // seeds) and its tab (useTabHash) once, while it is set up. The form is clean, so the leave
      // question lets this through.
      const failure = await router.replace({ name: formRoute ?? undefined, query, hash: "" }).catch(error => error)
      if ((failure && !isNavigationFailure(failure, NavigationFailureType.duplicated)) || !mounted) {
        handoff = null
        if (mounted) router.replace(to)
        return
      }
      createSeriesNotice.value = { id: ++noticeId, name, to }
      round.value++
    })
    return true
  }

  provide(CREATE_SERIES, { request: mode => (requested = mode) })

  return {
    seed,
    take: () => {
      const mode = requested ?? "open"
      requested = null
      return mode
    },
    snapshot: () => snapshot(state()),
    continue: continueSeries,
  }
}
